import axios from 'axios'
import qs from 'qs'
import extractErrors from 'utils/extractErrors'
import { isSecurityAuthRequestWithoutBearer } from 'utils/authSessionStorage'
import { store } from 'store'
import RefreshToken from 'api/refreshPromise'
import { reportApiErrorToSentry, reportInvalidApiPathToSentry } from 'api/reportApiErrorToSentry'
import { assertValidRequestUrl } from 'api/assertValidRequestUrl'
import { notificationShow } from 'modules/Notifications/actions'
import { LOGOUT } from 'modules/Auth/types'

const recordDevXhrFromAxios = (payload) => {
  if (process.env.NODE_ENV === 'production') {
    return
  }

  // eslint-disable-next-line global-require
  require('api/devXhrRecorder').recordDevXhrFromAxios(payload)
}

let logXhrBootstrapped = false

const ensureLogXhrEnabled = () => {
  if (process.env.NODE_ENV === 'production' || logXhrBootstrapped) {
    return
  }

  logXhrBootstrapped = true
  localStorage.setItem('log_xhr', 'true')
}

const isLogXhrEnabled = () =>
  process.env.NODE_ENV !== 'production' && localStorage.getItem('log_xhr') === 'true'

const setRequestHeader = (headers, name, value) => {
  if (!headers) {
    return
  }

  if (typeof headers.set === 'function') {
    headers.set(name, value)
    return
  }

  headers[name] = value
}

const isCanceledRequest = (error) => axios.isCancel(error) || error?.code === 'ERR_CANCELED'

const formatRequestPayload = (config) => {
  const payload = config?.payload ?? config?.data

  if (payload == null || payload === '') {
    return ''
  }

  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    return '[FormData]'
  }

  if (typeof payload === 'string') {
    try {
      return JSON.stringify(JSON.parse(payload), null, 2)
    } catch (e) {
      return payload
    }
  }

  try {
    return JSON.stringify(payload, null, 2)
  } catch (e) {
    return String(payload)
  }
}

class Api {
  constructor(config) {
    if (process.env.NODE_ENV !== 'production') {
      ensureLogXhrEnabled()
    }

    this.instance = axios.create({
      ...config,
      withCredentials: true,
    })

    if (process.env.NODE_ENV !== 'production') {
      // Dev-only: keep out of production bundle (mocks may import gitignored fixtures).
      // eslint-disable-next-line global-require
      require('api/mocks').default(this.instance)
    }

    this.instance.interceptors.request.use((config) => {
      try {
        const invalidPath = assertValidRequestUrl(config)

        if (invalidPath) {
          reportInvalidApiPathToSentry({
            method: String(config.method || 'get').toUpperCase(),
            path: invalidPath.path,
            page: window.location.pathname,
            invalidSegment: invalidPath.invalidSegment,
          })
        }
      } catch (e) {
        // logging must never break the request
      }

      const url = config.url || ''

      if (!isSecurityAuthRequestWithoutBearer(url)) {
        const switchedUser = localStorage.getItem('su')
        config.headers = config.headers || {}

        if (switchedUser) {
          setRequestHeader(config.headers, 'X-Switch-User', switchedUser)
        }

        const token = localStorage.getItem('access_token')
        if (token) {
          setRequestHeader(config.headers, 'Authorization', `Bearer ${token}`)
        }
      }

      return config
    })

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Do not spread `error.config`: axios 1 AxiosHeaders is lost on a shallow clone.
        const originalRequest = error.config

        if (
          error?.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !originalRequest.url.match(
            /(\/logout$)|(\/auth$)|(\/auth\/two_factor)|(\/admitad\/)|(\/register\/activate)|(\/password\/recover)|(\/refresh$)/,
          )
        ) {
          originalRequest._retry = true
          try {
            await RefreshToken.getInstance()
            const token = localStorage.getItem('access_token')
            if (token) {
              originalRequest.headers = originalRequest.headers || {}
              setRequestHeader(originalRequest.headers, 'Authorization', `Bearer ${token}`)
            }
            return this.instance.request(originalRequest)
          } catch (refreshError) {
            store.dispatch((() => ({ type: LOGOUT }))())
          }
        }

        throw error
      },
    )

    this.get = this.get.bind(this)
    this.put = this.put.bind(this)
    this.patch = this.patch.bind(this)
    this.post = this.post.bind(this)
    this.delete = this.delete.bind(this)
  }

  request(config) {
    return this.instance
      .request(config)
      .then((response) => {
        if (isLogXhrEnabled()) {
          try {
            const url_mask = localStorage.getItem('log_xhr_url_mask')

            if (!url_mask || new RegExp(url_mask).test(response.config.url)) {
              console.log('------START REQUEST----------')
              console.log(
                `Request method: %c${response.config.method.toUpperCase()}, %curl: %c${
                  response.config.baseURL + response.config.url
                }`,
                'color: blue; font-weight: bold;',
                'color: black',
                'color: red; font-weight: bold;',
              )

              console.log('Request params: \n \n', JSON.stringify(response.config.params, null, 2))
              console.log('Request payload data: \n \n', formatRequestPayload(response.config))
              console.log('Success response data: ', { ...response.data })
              console.groupCollapsed('Response as string (click me to uncollapse) =>')
              console.log(JSON.stringify(response.data, null, 2))
              console.groupEnd()
              console.log('Full response: ', { ...response })
              console.log('--------END REQUEST-----------')
              console.log('  ')
            }
          } catch (e) {
            console.log('Request log failed: ', e)
          }
        }

        recordDevXhrFromAxios({ config: response.config, response })

        return {
          response: {
            ...response.data,
            status: response.status,
            statusText: response.statusText,
          },
        }
      })
      .catch((error) => {
        if (isCanceledRequest(error)) {
          return {
            error: {
              canceled: true,
              status: 499,
              statusText: error.message,
            },
          }
        }

        reportApiErrorToSentry(error)

        if (error?.config) {
          recordDevXhrFromAxios({ config: error.config, error })
        }

        if (isLogXhrEnabled()) {
          try {
            console.log('Response error: ', error.toJSON())
          } catch (e) {
            console.log('Response error: ', error)
          }
        }

        if (error?.response?.status >= 500) {
          const { hash = null } = extractErrors(error.response?.data?.errors)

          !!hash &&
            store.dispatch(
              notificationShow({
                type: 'warning',
                message: hash,
                delay: 20000,
              }),
            )

          return {
            error: {
              status: error.response.status,
              statusText: error.message,
              errors: error.response?.data?.errors ?? [],
            },
          }
        }

        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          return {
            error: {
              status: 400,
              statusText: error.message + '\nurl: ' + error.config?.url,
            },
          }
        }

        if (error.code === 'ECONNABORTED' || error.response?.status === 408) {
          return {
            error: {
              status: 408,
              statusText: error.message,
            },
          }
        }

        if (error?.response?.status === 413) {
          return {
            error: {
              status: 413,
              statusText: error.response.statusText,
              errors: ['request_entity_too_large'],
            },
          }
        }

        if (!error.response) {
          return {
            error: {
              status: 0,
              statusText: error.message,
            },
          }
        }

        const data = error.response.data.data || error.response.data

        return {
          error: {
            ...data,
            status: error.response.status,
            statusText: error.response.statusText,
          },
        }
      })
  }

  get(url, payload = {}) {
    const config = {
      ...payload,
      method: 'GET',
      url,
    }

    if (payload.params) {
      config.paramsSerializer = {
        serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
      }
    }

    return this.request(config)
  }

  put(url, payload = {}) {
    return this.request({
      method: 'PUT',
      url,
      ...payload,
    })
  }

  post(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'POST',
      url,
    })
  }

  patch(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'PATCH',
      url,
    })
  }

  delete(url, payload = {}) {
    return this.request({
      ...payload,
      method: 'DELETE',
      url,
    })
  }
}

export default Api
